/**
 * TEMPORARY per-call voice diagnostics collector.
 *
 * While in a voice call, samples WebRTC getStats (local outbound + every
 * remote inbound) + denoiser-pipeline state every SAMPLE_MS and batches it
 * to POST /api/voice-diag/batch every FLUSH_MS, so an incident ("nobody can
 * hear anybody") can be reconstructed from SQL afterwards.
 *
 * Gated by the server kill-switch: on call connect we poll
 * GET /api/voice-diag/enabled ONCE; if false the collector does nothing
 * (zero overhead when off). Enable for an incident-debugging day, disable
 * afterwards — no client deploy needed to toggle.
 *
 * EVERYTHING here is best-effort: every step is wrapped so a telemetry
 * failure can NEVER affect the call. SECURITY: collects NO auth tokens /
 * cookies — only voice + WebRTC metrics and non-sensitive client context.
 */
import type { Room } from 'livekit-client'
import api from './api'
import { mediaManager } from './mediaManager'
import { getVoiceDiagnosticData } from './voiceDiagnostic'
import { APP_VERSION } from './version'
import { useAuthStore } from '../stores/authStore'
import { useVoiceStore } from '../stores/voiceStore'
import { getPeerLoudness } from './peerLoudness'
import { backgroundEngine } from './backgroundEngineFlag'
import { getBackgroundDiag } from './backgroundDiag'
import { getTranscriptionDiag } from './transcriptionDiag'
import { parseIce } from './voiceIceStats'

const SAMPLE_MS = 2000
const FLUSH_MS = 10000

interface Ctx {
  roomKind: 'channel' | 'dm'
  channelId: string
  userName: string
}

interface PrevBytes {
  outBytes: number | null
  outTs: number
  peers: Record<string, { bytes: number; ts: number }>
}

interface IceState {
  errorCount: number
  lastUrl: string
  lastCode: number
  lastText: string
  servers: string
  // errorCount already appended to rawStats as an 'ice-error' row.
  reportedErrors: number
  // PCs that already carry our icecandidateerror listener. LiveKit builds NEW
  // PeerConnections on every full reconnect, so listeners are (re)attached lazily
  // per sample instead of only once at collector start.
  attached: WeakSet<object>
}

interface Collector {
  stop: () => void
}

const collectors = new Map<string, Collector>()

export async function startVoiceTelemetry(room: Room, ctx: Ctx): Promise<void> {
  try {
    const key = ctx.channelId || ctx.roomKind
    if (collectors.has(key)) return

    // Kill-switch: one cheap GET. If disabled, never start.
    let enabled = false
    try {
      const r = await api.get('/voice-diag/enabled')
      enabled = !!(r?.data?.enabled)
    } catch {
      enabled = false
    }
    if (!enabled || collectors.has(key)) return

    const diagSessionId = `${ctx.channelId || 'room'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const prev: PrevBytes = { outBytes: null, outTs: 0, peers: {} }
    // Best-effort ICE diagnostics: listen for STUN/TURN candidate errors on
    // the underlying RTCPeerConnection(s) and snapshot the configured
    // iceServers. LiveKit internals are accessed defensively — if the path is
    // absent/changed we silently skip (getStats still gives relay/host type).
    const ice: IceState = {
      errorCount: 0, lastUrl: '', lastCode: 0, lastText: '', servers: '', reportedErrors: 0, attached: new WeakSet(),
    }
    attachIceListeners(room, ice)
    let buffer: any[] = []
    let sessionSent = false

    const flush = async () => {
      if (buffer.length === 0 && sessionSent) return
      const samples = buffer
      buffer = []
      const payload: any = {
        diagSessionId,
        roomKind: ctx.roomKind,
        channelId: ctx.channelId,
        userName: ctx.userName,
        samples,
      }
      if (!sessionSent) {
        payload.session = buildSessionContext(ice)
        sessionSent = true
      }
      try { await api.post('/voice-diag/batch', payload) } catch { /* best-effort */ }
    }

    const sampleTimer = setInterval(() => {
      collectSample(room, prev, ice)
        .then((s) => { if (s) buffer.push(s) })
        .catch(() => {})
    }, SAMPLE_MS)
    const flushTimer = setInterval(() => { flush().catch(() => {}) }, FLUSH_MS)

    collectors.set(key, {
      stop: () => {
        try { clearInterval(sampleTimer) } catch { /* noop */ }
        try { clearInterval(flushTimer) } catch { /* noop */ }
        collectors.delete(key)
        flush().catch(() => {}) // final flush of whatever's buffered
      },
    })
  } catch {
    // Never throw into the call connect path.
  }
}

// Stop a specific collector, or ALL of them when no key is given (used on
// disconnect, where only one channel room is ever active).
export function stopVoiceTelemetry(channelId?: string): void {
  try {
    if (channelId) {
      collectors.get(channelId)?.stop()
      return
    }
    for (const c of Array.from(collectors.values())) c.stop()
  } catch {
    // noop
  }
}

function buildSessionContext(ice: IceState): any {
  const d = getVoiceDiagnosticData()
  let netEffectiveType = ''
  let netDownlink = 0
  let netRtt = 0
  try {
    const c = (navigator as any).connection
    if (c) { netEffectiveType = c.effectiveType || ''; netDownlink = c.downlink || 0; netRtt = c.rtt || 0 }
  } catch { /* noop */ }
  let voicePrefs = '{}'
  try {
    voicePrefs = JSON.stringify({
      noiseSuppression: localStorage.getItem('noiseSuppression'),
      denoiserBackend: localStorage.getItem('denoiserBackend'),
      audioInputDeviceId: localStorage.getItem('audioInputDeviceId') ? 'set' : '',
      audioOutputDeviceId: localStorage.getItem('audioOutputDeviceId') ? 'set' : '',
    })
  } catch { /* noop */ }
  let screen = ''
  let timezone = ''
  try { screen = `${window.screen?.width}x${window.screen?.height}@${window.devicePixelRatio || 1}` } catch { /* noop */ }
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } catch { /* noop */ }
  return {
    tMs: Date.now(),
    appVersion: APP_VERSION,
    browser: d.browser.name,
    browserVersion: d.browser.version,
    platform: d.platform,
    ua: d.ua,
    hardwareConcurrency: d.hardwareConcurrency,
    hasAudioWorklet: d.audio.hasAudioWorklet,
    hasWasmSimd: d.wasm.hasSimd,
    hasModuleWorker: d.wasm.hasModuleWorker,
    probeSampleRate: d.audio.requested48kHz_got,
    ctxStateAtCreate: d.audio.ctxStateAtCreate,
    screen,
    timezone,
    netEffectiveType,
    netDownlink,
    netRtt,
    voicePrefs,
    iceServers: ice.servers,
  }
}

async function collectSample(room: Room, prev: PrevBytes, ice: IceState): Promise<any | null> {
  const sample: any = { tMs: Date.now() }
  // Build the user is actually running — answers "stale cached bundle?" from
  // SQL instead of asking them to read the version chip.
  sample.appVersion = APP_VERSION

  // Per-room denoiser state (forRoom), NOT the singleton getDenoiserState():
  // mic publishing moved to the per-room sharedMicCapture path, so the
  // singleton _denoiserState is stale (only the legacy health-reapply path
  // still sets it) → it mis-reported the backend for BOTH channel and DM.
  // The MediaContext carries the real per-room backend. Falls back to the
  // singleton read if the context lookup throws.
  try { sample.denoiserBackend = mediaManager.forRoom(room).getDenoiserState() }
  catch { try { sample.denoiserBackend = mediaManager.getDenoiserState() } catch { /* noop */ } }
  // Hoisted to function scope: the audio-gt-diag append block far below also reads
  // `c`. It was previously declared `const` INSIDE this try (block-scoped), so that
  // later read threw ReferenceError (swallowed by its own try/catch) → the
  // audio-gt-diag rawStats row NEVER persisted since 2026-06-15. Hoisting fixes it
  // and lets the new audio-desync-diag row use the same handle.
  let c: ReturnType<typeof mediaManager.getDiagnosticCounters> | null = null
  try {
    c = mediaManager.getDiagnosticCounters()
    sample.gumCount = c.gumCount
    sample.denoiserApplyCount = c.denoiserApplyCount
    sample.micTrackEndedCount = c.micTrackEndedCount
    sample.healthRecoveryCount = c.healthRecoveryCount
    sample.lastRecoveryReason = c.lastRecoveryReason
    // Routing-decision visibility — why DFN is/ isn't running, from SQL.
    sample.denoiserPref = c.denoiserPref
    sample.cpuPressure = c.cpuPressure
    sample.dfnCooldownMs = c.dfnCooldownMs
    sample.cpuVideoOverride = c.cpuVideoOverride
    sample.lastMicDisable = c.lastMicDisable
  } catch { /* noop */ }
  // (DFN inference metrics block removed with DFN itself in 1.1.177A.
  // voice_diag's `dfn_avg_ms` / `dfn_drops` columns stay in the CH schema
  // for backwards-compat with existing queries but are no longer populated.)
  try { sample.iceErrorCount = ice.errorCount } catch { /* noop */ }
  // Virtual-background (RVM) diagnostics (OSPROD-232). The matting pipeline's
  // internal state (model ready, runtime fallback, privacy-background-only
  // holding, matte fps, inference cost/errors) was console-only — so "person
  // invisible behind the virtual background" incidents weren't diagnosable from
  // SQL. Fold the decoupled snapshot (backgroundDiag, published by
  // backgroundProcessor) into each sample. null when no processor is live.
  try {
    const _bgDiag = getBackgroundDiag()
    sample.bgEngine = (() => { try { return backgroundEngine() } catch { return '' } })()
    sample.rvmReady = _bgDiag ? _bgDiag.ready : null
    sample.rvmFallback = _bgDiag ? _bgDiag.fallback : null
    sample.rvmPrivacyHolding = _bgDiag ? _bgDiag.privacyHolding : null
    sample.rvmMatteFps = _bgDiag ? _bgDiag.matteFps : null
    sample.rvmInferMs = _bgDiag ? _bgDiag.inferMs : null
    sample.rvmInferErrors = _bgDiag ? _bgDiag.inferErrors : null
    // OSPROD-232 bg-perf: which ONNX EP is live (wasm vs webgpu opt-in) + the
    // composite/paint fps — lets QA verify "background no longer lags" from CH
    // (rvm_composite_fps should track the camera fps, not collapse under it).
    sample.rvmEp = _bgDiag ? _bgDiag.ep : null
    sample.rvmCompositeFps = _bgDiag ? _bgDiag.compositeFps : null
  } catch { /* noop */ }
  // OSPROD-232 (camera-zoom-reset sub-bug): the user's manual reframe
  // (zoom/pan) + auto-face-center state, so a "my zoom reset on background
  // toggle" report can be confirmed from ClickHouse (does cam_zoom actually
  // drop back to 1, and did auto_face_center re-engage, and exactly when).
  try {
    sample.camZoom = mediaManager.getVideoZoom()
    const _pan = mediaManager.getVideoPan()
    sample.camPanX = _pan.x
    sample.camPanY = _pan.y
  } catch { /* noop */ }
  try {
    const _vs = useVoiceStore.getState()
    sample.autoFaceCenter = _vs.autoFaceCenter
    sample.autoFaceCenterSuppressed = _vs.autoFaceCenterSuppressedByBg
  } catch { /* noop */ }
  try {
    // Mute = per-room mic-publish ground truth (works for BOTH channel and DM).
    // The old read of authStore user.settings.isMuted reflected only the
    // global/channel mute pref, so a DM sample reported the WRONG mute state —
    // useless for "can't be heard": we couldn't tell intentional-mute from a
    // live-but-silent track. Deafen stays a global user pref.
    sample.isMuted = !room.localParticipant?.isMicrophoneEnabled
    sample.isDeafened = !!useAuthStore.getState().user?.settings?.isDeafened
    // PERSISTED mute INTENT (what the 1s health-check reconciler follows:
    // expectedMuted = settings.isMuted). is_muted above is the RUNTIME mic
    // state. Logging both makes the mute-coupling divergence directly
    // queryable: `intent_muted != is_muted` (allowing for deafen) is the
    // health-audio-desync flap root, instead of inferring it from
    // last_recovery_reason. See project_voice_mute_desync_2026_06_25.
    sample.intentMuted = !!useAuthStore.getState().user?.settings?.isMuted
  } catch { /* noop */ }
  try {
    const c = (navigator as any).connection
    if (c) { sample.netEffectiveType = c.effectiveType || ''; sample.netDownlink = c.downlink || 0; sample.netRtt = c.rtt || 0 }
  } catch { /* noop */ }

  // Local outbound audio
  try {
    const lp: any = room.localParticipant
    const pub = lp && lp.audioTrackPublications ? Array.from(lp.audioTrackPublications.values())[0] as any : null
    const track = pub?.track
    if (track?.getRTCStatsReport) {
      const report = await track.getRTCStatsReport()
      if (report) {
        const local = parseLocalAudio(report, prev)
        Object.assign(sample, local.fields)
        sample.rawStats = local.raw
        // Fallback only — overwritten below by the PeerConnection-level report.
        try { Object.assign(sample, parseIce(report)) } catch { /* noop */ }
      }
      try {
        const ms = track.mediaStreamTrack
        if (ms?.getSettings) sample.micDevice = ms.getSettings().deviceId || ''
      } catch { /* noop */ }
    }
  } catch { /* noop */ }

  // ICE/transport from the PeerConnection itself (all candidates, works while
  // muted) — see voiceIceStats.ts for why the mic sender report is not enough.
  try {
    attachIceListeners(room, ice)
    const iceFields = await collectIce(room)
    if (iceFields) Object.assign(sample, iceFields)
  } catch { /* noop */ }

  // Remote inbound audio (per peer)
  try {
    const inbound: any[] = []
    let silent = 0
    const remotes: any[] = room.remoteParticipants ? Array.from((room.remoteParticipants as any).values()) : []
    for (const rp of remotes) {
      try {
        const pub = rp.audioTrackPublications ? Array.from(rp.audioTrackPublications.values())[0] as any : null
        const track = pub?.track
        if (!track?.getRTCStatsReport) continue
        const report = await track.getRTCStatsReport()
        if (!report) continue
        const r = parseRemoteAudio(report, prev, String(rp.identity || rp.sid || ''))
        r.id = String(rp.identity || '')
        r.name = String(rp.name || '')
        // Our own measured ARRIVING loudness for this peer (dBFS), from
        // LiveKitBackgroundAudio's source-tap loop. This is the [loudness]
        // value, now persisted to ClickHouse instead of console-only. null when
        // the peer has been silent (stale) — distinct from a real 0.
        r.loudnessDbfs = getPeerLoudness(r.id)
        inbound.push(r)
        if ((r.audioLevel ?? 0) < 0.001) silent++
      } catch { /* skip this peer */ }
    }
    sample.remoteCount = inbound.length
    sample.inboundSilentCount = silent
    sample.inbound = JSON.stringify(inbound)
  } catch { /* noop */ }

  // VIDEO diagnostics (2026-06-15). The raw blob above is audio-only, so the
  // recurring "cameras flicker" reports were invisible to telemetry — the
  // freeze/resolution/quality-limitation signal lives on the VIDEO tracks
  // (separate publications). Append compact send + per-peer recv video stats
  // to rawStats (queryable by type/kind), so the next big call's flicker is
  // diagnosable like the May audio incident was. Best-effort; gated by the
  // same VOICE_DIAG kill-switch as the rest of the collector.
  try {
    const videoObjs = await collectVideoStats(room)
    if (videoObjs.length) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, ...videoObjs])
    }
  } catch { /* noop */ }

  // AUDIO health-audio-desync diagnostic (2026-06-15). When a false audio zombie
  // fired, mediaManager recorded the verifyGroundTruth inputs; append them to
  // rawStats (zero CH schema change) so the universal health-audio-desync churn
  // can be pinpointed — i.e. WHY the 154A denoiser-handle guard missed.
  try {
    if (c?.lastAudioGtDiag) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'audio-gt-diag', v: c.lastAudioGtDiag }])
    }
  } catch { /* noop */ }

  // AUDIO flag-false desync diagnostic (2026-06-29). The OTHER desync branch
  // (isMicrophoneEnabled=false while intent=unmuted) — the one that actually drove
  // the 25-person standup's universal health-audio-desync churn — now records its
  // inputs (flag/pub/track/denoiser/room-state). Append to rawStats (zero CH schema
  // change) so the NEXT call pinpoints publish-failed vs ICE-never-connected vs
  // false-ended, instead of guessing. Queryable: type='audio-desync-diag'.
  try {
    if (c?.lastAudioDesyncDiag) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'audio-desync-diag', v: c.lastAudioDesyncDiag }])
    }
  } catch { /* noop */ }

  // Mic-toggle failure diagnostic (2026-07-01, OSPROD-248). Every store-level
  // toggle failure ("Microphone toggle failed" toast) records its stage +
  // underlying error into mediaManager; append it to rawStats (zero CH schema
  // change) so the failure REASON is queryable: type='mic-toggle-fail'.
  try {
    if (c?.lastMicToggleFail) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'mic-toggle-fail', v: c.lastMicToggleFail, n: c.micToggleFailCount }])
    }
  } catch { /* noop */ }

  // Transcription recovery diagnostic (2026-07-06, OSPROD-267). Every
  // recorder restart (chunk-flow stall, track swap, visibility recovery,
  // server session-dead re-arm) + history backfill records its counters
  // into transcriptionDiag; append them to rawStats (zero CH schema
  // change) so "transcription froze / lines missing" is queryable:
  // type='transcription-diag'.
  try {
    const td = getTranscriptionDiag()
    if (td) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'transcription-diag', v: td }])
    }
  } catch { /* noop */ }

  // Camera-enable stall diagnostic (2026-07-06, OSPROD-271). A camera-enable
  // attempt that exceeded the watchdog (stuck pre-publish await — the
  // "camera never starts while Recording is active" infinite spinner)
  // records its stage + attempt into mediaManager; append it to rawStats
  // (zero CH schema change) so the blocking await is queryable:
  // type='camera-enable-stall'.
  try {
    if (c?.lastCamEnableStall) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'camera-enable-stall', v: c.lastCamEnableStall, n: c.camEnableStallCount }])
    }
  } catch { /* noop */ }

  // Camera-enable STAGE TIMING (OSPROD-271 round 2). The stall flag above says
  // an attempt blew the 10s budget but not which await held it, so the first
  // fix bounded the hang without ever locating it — and the retest showed the
  // cost had merely turned into a deterministic 15-20s. This carries the
  // per-stage breakdown (import-lk / unpublish-existing / gum / import-zoom /
  // setProcessor / publish) plus the outcome for every enable slower than
  // CAMERA_ENABLE_SLOW_MS: type='camera-enable-timing'.
  try {
    if (c?.lastCamEnableTiming) {
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'camera-enable-timing', v: c.lastCamEnableTiming, n: c.camEnableSlowCount }])
    }
  } catch { /* noop */ }

  // STUN/TURN candidate errors (2026-10-15 TURN audit). ice_error_count was > 0
  // in ~28% of sessions but WHICH server failed and HOW was never persisted. Append
  // the last error only when the count grew (zero CH schema change):
  // type='ice-error' → {url, code, text, n}.
  try {
    if (ice.errorCount > ice.reportedErrors) {
      ice.reportedErrors = ice.errorCount
      let base: any[] = []
      try { base = sample.rawStats ? JSON.parse(sample.rawStats) : [] } catch { base = [] }
      sample.rawStats = JSON.stringify([...base, { type: 'ice-error', url: ice.lastUrl, code: ice.lastCode, text: ice.lastText, n: ice.errorCount }])
    }
  } catch { /* noop */ }

  return sample
}

// Compact per-call VIDEO stats — send side (local camera/screen encoder) +
// receive side (each subscribed remote video). The receive freezeCount /
// framesPerSecond / frameWidth identify decode-side flicker; the send
// qualityLimitationReason ('cpu'/'bandwidth') + framesPerSecond identify a
// struggling encoder (e.g. the RVM background canvas pipeline). Capped to
// bound per-tick getStats cost on big all-camera calls.
async function collectVideoStats(room: Room): Promise<any[]> {
  const out: any[] = []
  // Send side: local video publications (camera + screen share).
  try {
    const lp: any = room.localParticipant
    const pubs: any[] = lp?.videoTrackPublications ? Array.from(lp.videoTrackPublications.values()) : []
    let bg = ''
    try { bg = backgroundEngine() } catch { /* noop */ }
    for (const pub of pubs) {
      const track = pub?.track
      if (!track?.getRTCStatsReport) continue
      const report = await track.getRTCStatsReport()
      if (!report) continue
      report.forEach((s: any) => {
        if (s.type === 'outbound-rtp' && s.kind === 'video') {
          out.push({
            type: 'outbound-rtp', kind: 'video', dir: 'send',
            source: String(pub.source || ''), bgEngine: bg, rid: s.rid,
            frameWidth: s.frameWidth, frameHeight: s.frameHeight, framesPerSecond: s.framesPerSecond,
            framesEncoded: s.framesEncoded, framesSent: s.framesSent,
            qualityLimitationReason: s.qualityLimitationReason,
            qualityLimitationDurations: s.qualityLimitationDurations,
          })
        } else if (s.type === 'media-source' && s.kind === 'video') {
          out.push({
            type: 'media-source', kind: 'video', dir: 'send',
            width: s.width, height: s.height, framesPerSecond: s.framesPerSecond, frames: s.frames,
          })
        }
      })
    }
  } catch { /* noop */ }
  // Receive side: subscribed remote video (cap to bound big-call overhead).
  try {
    const remotes: any[] = room.remoteParticipants ? Array.from((room.remoteParticipants as any).values()) : []
    let count = 0
    for (const rp of remotes) {
      if (count >= 16) break
      const pubs: any[] = rp.videoTrackPublications ? Array.from(rp.videoTrackPublications.values()) : []
      for (const pub of pubs) {
        const track = pub?.track
        if (!track?.getRTCStatsReport) continue // only subscribed+playing tracks have a report
        const report = await track.getRTCStatsReport()
        if (!report) continue
        count++
        report.forEach((s: any) => {
          if (s.type === 'inbound-rtp' && s.kind === 'video') {
            out.push({
              type: 'inbound-rtp', kind: 'video', dir: 'recv',
              peerId: String(rp.identity || ''), peerName: String(rp.name || ''),
              source: String(pub.source || ''),
              frameWidth: s.frameWidth, frameHeight: s.frameHeight, framesPerSecond: s.framesPerSecond,
              framesDecoded: s.framesDecoded, framesDropped: s.framesDropped, framesReceived: s.framesReceived,
              freezeCount: s.freezeCount, totalFreezesDuration: s.totalFreezesDuration,
              pauseCount: s.pauseCount, totalPausesDuration: s.totalPausesDuration,
            })
          }
        })
      }
    }
  } catch { /* noop */ }
  return out
}

function parseLocalAudio(report: any, prev: PrevBytes): { fields: any; raw: string } {
  let audioLevel = 0
  let totalEnergy = 0
  let bytesSent = 0
  let packetsSent = 0
  let packetsLost = 0
  let rttMs = 0
  let tStamp = 0
  const rawObjs: any[] = []
  try {
    report.forEach((s: any) => {
      if (s.type === 'media-source' && s.kind === 'audio') {
        if (s.audioLevel != null) audioLevel = s.audioLevel
        if (s.totalAudioEnergy != null) totalEnergy = s.totalAudioEnergy
        rawObjs.push(s)
      } else if (s.type === 'outbound-rtp' && s.kind === 'audio') {
        if (s.bytesSent != null) bytesSent = s.bytesSent
        if (s.packetsSent != null) packetsSent = s.packetsSent
        if (s.timestamp != null) tStamp = s.timestamp
        rawObjs.push(s)
      } else if (s.type === 'remote-inbound-rtp' && s.kind === 'audio') {
        if (s.packetsLost != null) packetsLost = s.packetsLost
        if (s.roundTripTime != null) rttMs = s.roundTripTime * 1000
        rawObjs.push(s)
      } else if (s.type === 'candidate-pair' && (s.nominated || s.state === 'succeeded')) {
        if (!rttMs && s.currentRoundTripTime != null) rttMs = s.currentRoundTripTime * 1000
        rawObjs.push(s)
      }
    })
  } catch { /* noop */ }

  let kbps = 0
  if (prev.outBytes != null && tStamp > prev.outTs && prev.outTs > 0) {
    kbps = ((bytesSent - prev.outBytes) * 8) / ((tStamp - prev.outTs) / 1000) / 1000
  }
  prev.outBytes = bytesSent
  prev.outTs = tStamp || Date.now()

  return {
    fields: {
      localAudioLevel: round(audioLevel, 4),
      localTotalEnergy: totalEnergy,
      outBitrateKbps: kbps > 0 ? round(kbps, 1) : 0,
      outPacketsSent: packetsSent,
      outPacketsLost: packetsLost,
      outRttMs: round(rttMs, 1),
    },
    raw: safeJson(rawObjs),
  }
}

function parseRemoteAudio(report: any, prev: PrevBytes, peerId: string): any {
  let audioLevel = 0
  let bytesReceived = 0
  let packetsLost = 0
  let jitterMs = 0
  let tStamp = 0
  try {
    report.forEach((s: any) => {
      if (s.type === 'inbound-rtp' && s.kind === 'audio') {
        if (s.audioLevel != null) audioLevel = s.audioLevel
        if (s.bytesReceived != null) bytesReceived = s.bytesReceived
        if (s.packetsLost != null) packetsLost = s.packetsLost
        if (s.jitter != null) jitterMs = s.jitter * 1000
        if (s.timestamp != null) tStamp = s.timestamp
      }
    })
  } catch { /* noop */ }

  if (!prev.peers) prev.peers = {}
  const p = prev.peers[peerId]
  let kbps = 0
  if (p && p.bytes != null && tStamp > p.ts && p.ts > 0) {
    kbps = ((bytesReceived - p.bytes) * 8) / ((tStamp - p.ts) / 1000) / 1000
  }
  prev.peers[peerId] = { bytes: bytesReceived, ts: tStamp || Date.now() }

  return {
    audioLevel: round(audioLevel, 4),
    bitrateKbps: kbps > 0 ? round(kbps, 1) : 0,
    packetsLost,
    jitterMs: round(jitterMs, 1),
  }
}

function round(n: number, d: number): number {
  const f = Math.pow(10, d)
  return Math.round((n || 0) * f) / f
}

function safeJson(v: any): string {
  try { return JSON.stringify(v) } catch { return '' }
}

// Best-effort access to LiveKit's underlying RTCPeerConnection(s). All
// `@internal` — wrapped + skipped if the path changes across versions.
function getPcs(room: any): RTCPeerConnection[] {
  const pcs: RTCPeerConnection[] = []
  try {
    const eng = room?.engine
    const pm = eng?.pcManager
    const cands = [pm?.publisher?.pc, pm?.subscriber?.pc, eng?.publisher?.pc, eng?.subscriber?.pc]
    for (const c of cands) {
      if (c && typeof c.addEventListener === 'function' && !pcs.includes(c)) pcs.push(c)
    }
  } catch { /* noop */ }
  return pcs
}

// (Re)attach the icecandidateerror listener + snapshot iceServers on every LiveKit
// PeerConnection not seen yet. Called at collector start AND per sample: a full
// LiveKit reconnect replaces the PCs, and the old one-shot attach went deaf after it.
function attachIceListeners(room: any, ice: IceState): void {
  for (const pc of getPcs(room)) {
    if (ice.attached.has(pc)) continue
    ice.attached.add(pc)
    try {
      pc.addEventListener('icecandidateerror', (ev: any) => {
        ice.errorCount++
        ice.lastUrl = String(ev?.url || '')
        ice.lastCode = Number(ev?.errorCode) || 0
        ice.lastText = String(ev?.errorText || '')
      })
    } catch { /* noop */ }
    try {
      const cfg = (pc as any).getConfiguration?.()
      if (cfg?.iceServers && !ice.servers) {
        ice.servers = JSON.stringify(cfg.iceServers.map((s: any) => s.urls))
      }
    } catch { /* noop */ }
  }
}

// ICE fields from the full pc.getStats() of the connection that carries what
// the user HEARS: the subscriber PC (LiveKit's primary — connected even for a
// muted listener who never published), else the publisher PC (single-PC mode).
async function collectIce(room: any): Promise<ReturnType<typeof parseIce> | null> {
  const eng = room?.engine
  const pm = eng?.pcManager
  const ordered = [pm?.subscriber?.pc, eng?.subscriber?.pc, pm?.publisher?.pc, eng?.publisher?.pc]
  for (const pc of ordered) {
    if (!pc || typeof pc.getStats !== 'function' || pc.connectionState === 'closed') continue
    return parseIce(await pc.getStats())
  }
  return null
}
